import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth.tsx';
import { EmailSignupForm } from '@/components/auth/EmailSignupForm';
import { EmailPasswordForm } from '@/components/auth/EmailPasswordForm';
import { ArrowLeft, Mail, Lock } from 'lucide-react';
import { UTMLink } from '@/components/custom-ui/utm-link';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Google icon
const GoogleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="18px" height="18px" className="mr-2">
    <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
    <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
    <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.574l6.19,5.238C39.71,35.96,44,30.607,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
  </svg>
);

// Facebook icon
// const FacebookIcon = () => (
//   <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36" fill="url(#a)" height="18" width="18" className="mr-2"><defs><linearGradient x1="50%" x2="50%" y1="97.078%" y2="0%" id="a"><stop offset="0%" stopColor="#0062E0"/><stop offset="100%" stopColor="#19AFFF"/></linearGradient></defs><path d="M15 35.8C6.5 34.3 0 26.9 0 18 0 8.1 8.1 0 18 0s18 8.1 18 18c0 8.9-6.5 16.3-15 17.8l-1-.8h-4l-1 .8z"/><path fill="#FFF" d="m25 23 .8-5H21v-3.5c0-1.4.5-2.5 2.7-2.5H26V7.4c-1.3-.2-2.7-.4-4-.4-4.1 0-7 2.5-7 7v4h-4.5v5H15v12.7c1 .2 2 .3 3 .3s2-.1 3-.3V23h4z"/></svg>
// );

// Email icon
const EmailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
  </svg>
);

// For preserving UTM parameters
const Link = UTMLink;

interface AuthOptionsProps {
  mode?: 'login' | 'signup';
  showLoginLink?: boolean;
  showTermsFooter?: boolean;
  onSuccess?: () => void;
  className?: string;
}

export function AuthOptions({ 
  mode = 'signup', 
  showLoginLink = true, 
  showTermsFooter = true,
  onSuccess,
  className = ''
}: AuthOptionsProps) {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const { signInWithGoogle
    //, signInWithFacebook 
    } = useAuth();

  const handleGoogleAuth = async () => {
    try {
      await signInWithGoogle();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Google auth error:', error);
    }
  };

  // const handleFacebookAuth = async () => {
  //   try {
  //     await signInWithFacebook();
  //     if (onSuccess) onSuccess();
  //   } catch (error) {
  //     console.error('Facebook auth error:', error);
  //   }
  // };

  const handleEmailClick = () => {
    setShowEmailForm(true);
  };

  const isLogin = mode === 'login';
  const title = isLogin ? 'Log in to your account' : 'Sign up for a free 14-day trial';
  const emailTitle = isLogin ? 'Log in with email' : 'Sign up with email';

  return (
    <div className={`flex flex-col ${className}`}>
      {showEmailForm ? (
        <>
          <div className="mb-4 text-center">
            <h2 className="text-2xl font-bold mb-4">{emailTitle}</h2>
          </div>
          <Button 
            variant="ghost" 
            className="mb-4 flex items-center gap-1 w-fit"
            onClick={() => setShowEmailForm(false)}
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          
          <Tabs defaultValue="password" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="password" className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Password
              </TabsTrigger>
              <TabsTrigger value="magic-link" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Magic Link
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="password">
              <EmailPasswordForm mode={mode} onSuccess={onSuccess} />
            </TabsContent>
            
            <TabsContent value="magic-link">
              <EmailSignupForm onSuccess={onSuccess} />
            </TabsContent>
          </Tabs>
        </>
      ) : (
        <>
          <div className="mb-4 text-center">
            <h2 className="text-2xl font-bold mb-4">{title}</h2>
          </div>
          <div className="space-y-3 flex flex-col items-center w-full max-w-xs mx-auto">
            {showLoginLink && !isLogin && (
              <Link to="/login">
                <Button variant="gooeyRight" className="w-full justify-center py-3 border-gray-300 dark:border-gray-700 bg-background dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-foreground">
                  Already have an account? &nbsp;<span className="font-medium text-blue-500 hover:underline">Login</span>
                </Button>
              </Link>
            )}
            {showLoginLink && isLogin && (
              <Link to="/signup">
                <Button variant="gooeyRight" className="w-full justify-center py-3 border-gray-300 dark:border-gray-700 bg-background dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-foreground">
                  Don't have an account? &nbsp;<span className="font-medium text-blue-500 hover:underline">Sign up</span>
                </Button>
              </Link>
            )}
            <Button 
              variant="outline" 
              className="w-full justify-center py-3 border-gray-300 dark:border-gray-700 bg-background dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-foreground"
              onClick={handleGoogleAuth}
            >
              <GoogleIcon /> Continue with Google
            </Button>
            {/* Facebook login button temporarily hidden
            <Button 
              variant="default" 
              className="w-full justify-center py-3 bg-[#1877F2] text-white hover:bg-[#1877F2]/90"
              onClick={handleFacebookAuth}
            >
              <FacebookIcon /> Continue with Facebook
            </Button>
            */}
            <Button 
              variant="outline" 
              className="w-full justify-center py-3 border-gray-300 dark:border-gray-700 bg-background dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-foreground"
              onClick={handleEmailClick}
            >
              <EmailIcon /> Continue with Email
            </Button>
          </div>
          
          {showTermsFooter && (
            <div className="mt-auto pt-4">
              <p className="text-xs text-muted-foreground text-center w-full leading-relaxed">
                By continuing, you agree to Rainfactory's{' '}
                <Link to="/terms" className="underline hover:text-primary">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="underline hover:text-primary">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
} 